import React, { useCallback } from "react";
import { useTranslations } from "@src/packages/i18n";
import { useBasicInfo } from "../BasicInfoContext";
import {
  BasicInfoQuestionLayout,
  DiagnosisStatusOptions,
  type DiagnosisStatus,
} from "../components";
import { useNavigation } from "@react-navigation/native";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@src/packages/patients/sync/usePatientSync";

export function EndometriosisScreen() {
  const t = useTranslations();
  const { state, updateState, next } = useBasicInfo("Endometriosis");
  const navigation = useNavigation();
  const { data: patient } = usePatient();
  const { mutateAsync: updatePatient, isPending } = useQueuedPatientUpdate();

  const selection: DiagnosisStatus | null =
    state.hasEndometriosis === "yes_diagnosed"
      ? "diagnosed"
      : state.hasEndometriosis === "yes_undiagnosed"
        ? "suspected"
        : state.hasEndometriosis === "no"
          ? "no"
          : null;

  const handleSelection = useCallback(
    async (val: DiagnosisStatus) => {
      const hasEndometriosis:
        "yes_diagnosed" | "yes_undiagnosed" | "no" | null =
        val === "diagnosed"
          ? "yes_diagnosed"
          : val === "suspected"
            ? "yes_undiagnosed"
            : val === "no"
              ? "no"
              : null;

      const stateUpdates = { hasEndometriosis };
      updateState(stateUpdates);
      try {
        await updatePatient({
          medicalHistory: {
            id: patient?.medicalHistory?.id,
            hasEndometriosis,
          },
        });
      } catch {
        return;
      }

      if (selection === null) {
        next(stateUpdates);
      }
    },
    [next, selection, updateState, updatePatient, patient?.medicalHistory?.id],
  );

  return (
    <BasicInfoQuestionLayout
      route="Endometriosis"
      title={t("med_history.basic_info.endometriosis.title")}
      helpText={t("med_history.basic_info.help.endometriosis")}
      onBack={() => navigation.goBack()}
      onSave={() => next()}
      saveDisabled={selection === null || isPending}
      hideSaveButton={selection === null}
      saveLabel={t("med_history.shared.next")}
    >
      <DiagnosisStatusOptions
        diagnosedLabel={t("med_history.shared.yes_diagnosed")}
        noLabel={t("med_history.shared.no")}
        onChange={handleSelection}
        suspectedLabel={t("med_history.shared.maybe_undiagnosed")}
        value={selection}
      />
    </BasicInfoQuestionLayout>
  );
}
